'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

function CounterNumber({ target, suffix = '' }: {target: string;suffix?: string;}) {
  const [displayed, setDisplayed] = useState('0');
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !animated.current) {
          animated.current = true;
          const num = parseInt(target.replace(/[^0-9]/g, ''));
          const duration = 1500;
          const steps = 40;
          let step = 0;
          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            const current = Math.floor(num * Math.min(progress, 1));
            setDisplayed(current.toLocaleString('en-IN') + (target.includes('+') ? '+' : ''));
            if (step >= steps) clearInterval(timer);
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <div ref={ref} className="tabular-nums">{displayed}</div>;
}

export default function HomePage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const kn = language === 'kn';

  const impactStats = [
  { number: '1,200+', label: t.home.stats.students, icon: '🎓' },
  { number: '60+', label: t.home.stats.scholarships, icon: '📚' },
  { number: '200+', label: t.home.stats.teachers, icon: '👩‍🏫' },
  { number: '45+', label: t.home.stats.communities, icon: '🏘️' }];


  const programs = [
  {
    title: kn ? 'ಶಿಕ್ಷಣ ಕಾರ್ಯಕ್ರಮ' : 'Education Program',
    desc: kn ? 'ರಚನಾತ್ಮಕ ಶಾಲೆಯ ನಂತರದ ಕಾರ್ಯಕ್ರಮಗಳು, ಡಿಜಿಟಲ್ ಸಾಕ್ಷರತೆ ಮತ್ತು ಮಾರ್ಗದರ್ಶನದ ಮೂಲಕ ಅಸಹಾಯಕ ಮಕ್ಕಳಿಗೆ ಕಲಿಕೆಯ ಅಂತರ ಕಡಿಮೆ ಮಾಡುವುದು.' : 'Bridging learning gaps for underprivileged children through structured after-school programs, digital literacy, and mentorship.',
    color: 'from-saffron to-saffron-dark', bg: 'bg-saffron-pale', border: 'border-saffron/20', icon: '📖', href: '/programs'
  },
  {
    title: kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನ' : 'Scholarships',
    desc: kn ? 'ಅರ್ಹ ವಿದ್ಯಾರ್ಥಿಗಳಿಗೆ ಆರ್ಥಿಕ ಅಡೆತಡೆಗಳಿಲ್ಲದೆ ಉನ್ನತ ಶಿಕ್ಷಣ ಪಡೆಯಲು ಅನುವು ಮಾಡಿಕೊಡುವ ಮೆರಿಟ್ ಮತ್ತು ಅಗತ್ಯ ಆಧಾರಿತ ವಿದ್ಯಾರ್ಥಿವೇತನಗಳು.' : 'Merit and need-based scholarships enabling deserving students to pursue higher education without financial barriers.',
    color: 'from-crimson to-crimson-dark', bg: 'bg-crimson-pale', border: 'border-crimson/20', icon: '🏆', href: '/programs'
  },
  {
    title: kn ? 'ಶಿಕ್ಷಕ ತರಬೇತಿ' : 'Teacher Training',
    desc: kn ? 'ಆಧುನಿಕ ಶಿಕ್ಷಣ ವಿಧಾನ, ತಂತ್ರಜ್ಞಾನ ಏಕೀಕರಣ ಮತ್ತು ಮಕ್ಕಳ ಕೇಂದ್ರಿತ ಬೋಧನಾ ವಿಧಾನಗಳೊಂದಿಗೆ ಸರ್ಕಾರಿ ಶಾಲಾ ಶಿಕ್ಷಕರ ಕೌಶಲ್ಯ ಅಭಿವೃದ್ಧಿ.' : 'Upskilling government school teachers with modern pedagogy, technology integration, and child-centric teaching methods.',
    color: 'from-indigo to-indigo-dark', bg: 'bg-indigo-pale', border: 'border-indigo/20', icon: '🎯', href: '/programs'
  },
  {
    title: kn ? 'ಸಮುದಾಯ ಆರೋಗ್ಯ' : 'Community Health',
    desc: kn ? 'ಬೆಂಗಳೂರಿನಾದ್ಯಂತ ಅಸಹಾಯಕ ಸಮುದಾಯಗಳನ್ನು ತಲುಪುವ ಆರೋಗ್ಯ ಶಿಬಿರಗಳು, ಜಾಗೃತಿ ಅಭಿಯಾನಗಳು ಮತ್ತು ತಡೆಗಟ್ಟುವ ಆರೈಕೆ ಕಾರ್ಯಕ್ರಮಗಳು.' : 'Health camps, awareness drives, and preventive care programs reaching underserved communities across Bengaluru.',
    color: 'from-gold to-gold-dark', bg: 'bg-amber-50', border: 'border-amber-200', icon: '❤️', href: '/programs'
  },
  {
    title: kn ? 'ಮಹಿಳಾ ಸಬಲೀಕರಣ' : 'Women Empowerment',
    desc: kn ? 'ಆರ್ಥಿಕ ಸ್ವಾತಂತ್ರ್ಯಕ್ಕಾಗಿ ಮಹಿಳೆಯರು ಮತ್ತು ಯುವಕರನ್ನು ಸಬಲೀಕರಿಸುವ ಕೌಶಲ್ಯ ಅಭಿವೃದ್ಧಿ, ಸ್ವಸಹಾಯ ಗುಂಪುಗಳು ಮತ್ತು ಜೀವನೋಪಾಯ ಕಾರ್ಯಕ್ರಮಗಳು.' : 'Skill development, self-help groups, and livelihood programs empowering women and youth for economic independence.',
    color: 'from-purple-600 to-purple-800', bg: 'bg-purple-50', border: 'border-purple-200', icon: '💪', href: '/programs'
  }];


  const fundItems = [
  { label: kn ? 'ಶಿಕ್ಷಣ ಕಾರ್ಯಕ್ರಮಗಳು' : 'Education Programs', pct: 55, color: 'bg-saffron' },
  { label: kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನಗಳು' : 'Scholarships', pct: 20, color: 'bg-crimson' },
  { label: kn ? 'ಸಮುದಾಯ ಆರೋಗ್ಯ' : 'Community Health', pct: 15, color: 'bg-gold' },
  { label: kn ? 'ಆಡಳಿತ ಮತ್ತು ಕಾರ್ಯಾಚರಣೆ' : 'Admin & Operations', pct: 10, color: 'bg-white/40' }];


  const testimonials = [
  { quote: kn ? "ಅರಿವು ಫೌಂಡೇಶನ್‌ನ ವಿದ್ಯಾರ್ಥಿವೇತನ ನನ್ನ ಜೀವನ ಬದಲಾಯಿಸಿತು. ನಾನು ಈಗ ಉನ್ನತ ಕಾಲೇಜಿನಲ್ಲಿ ಎಂಜಿನಿಯರಿಂಗ್ ಓದುತ್ತಿದ್ದೇನೆ." : "Arivu Foundation's scholarship changed my life. I am now pursuing engineering at a top college.", name: 'Priya Sharma', role: kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನ ಸ್ವೀಕರ್ತ, 2023' : 'Scholarship Recipient, 2023', initial: 'P' },
  { quote: kn ? "ಶಿಕ್ಷಕ ತರಬೇತಿ ಕಾರ್ಯಕ್ರಮ ನನ್ನ ವಿದ್ಯಾರ್ಥಿಗಳೊಂದಿಗೆ ನಾನು ಸಂವಹಿಸುವ ರೀತಿಯನ್ನು ಬದಲಾಯಿಸಿತು. ನಿಜವಾಗಿಯೂ ಪ್ರಭಾವಶಾಲಿ." : "The teacher training program transformed how I engage with my students. Truly impactful.", name: 'Ramesh Kumar', role: kn ? 'ಸರ್ಕಾರಿ ಶಾಲಾ ಶಿಕ್ಷಕ, ಬೆಂಗಳೂರು' : 'Government School Teacher, Bengaluru', initial: 'R' },
  { quote: kn ? "CSR ಪಾಲುದಾರರಾಗಿ, ನಾವು ಪಾರದರ್ಶಕ ಪ್ರಭಾವ ವರದಿ ಮತ್ತು ನಿಜವಾದ ಸಮುದಾಯ ಬದಲಾವಣೆ ನೋಡಿದ್ದೇವೆ." : "As a CSR partner, we've seen transparent impact reporting and genuine community change.", name: 'Anita Rao', role: kn ? 'CSR ಮುಖ್ಯಸ್ಥ, ತಂತ್ರಜ್ಞಾನ ಕಂಪನಿ' : 'CSR Head, Tech Company', initial: 'A' }];


  return (
    <div className="bg-white min-h-screen">
      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="relative min-h-screen flex items-center hero-gradient overflow-hidden pt-24">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
          </div>
          <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-saffron/10 blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-crimson/10 blur-3xl"></div>

          <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-medium mb-8 backdrop-blur-sm ${kn ? 'font-kannada' : ''}`}>
                <span className="w-2 h-2 rounded-full bg-saffron animate-pulse-slow"></span>
                {t.home.badge}
              </div>
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4 ${kn ? 'font-kannada' : ''}`}>
                {t.home.heroTitle1}<br />
                <span className="text-saffron-light">{t.home.heroTitle2}</span><br />
                {t.home.heroTitle3}
              </h1>
              <p className={`text-white/75 text-lg leading-relaxed mb-10 max-w-lg ${kn ? 'font-kannada' : ''}`}>
                {t.home.heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/donate" className={`px-8 py-4 bg-saffron text-white font-bold rounded-full text-base hover:bg-saffron-light transition-all shadow-saffron hover:shadow-lg hover:-translate-y-1 ${kn ? 'font-kannada' : ''}`}>
                  {t.home.donateBtn}
                </Link>
                <Link href="/about" className={`px-8 py-4 bg-white/10 text-white font-semibold rounded-full text-base border border-white/30 hover:bg-white/20 transition-all backdrop-blur-sm ${kn ? 'font-kannada' : ''}`}>
                  {t.home.ourStory}
                </Link>
              </div>
              <div className="flex flex-wrap gap-4 mt-10">
                {[t.home.badge12a, t.home.badge80g, t.home.badgeTrust].map((badge) =>
                <div key={badge} className={`flex items-center gap-2 text-white/60 text-sm ${kn ? 'font-kannada' : ''}`}>
                    <svg className="w-4 h-4 text-saffron" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {badge}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="w-74 h-74 sm:w-80 sm:h-80 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center p-8 animate-float">
                                  <div className="relative w-full h-full rounded-2xl overflow-hidden bg-white">
                                                      <Image src="/assets/images/Indian_children_telling_namaskar_202605161456-1778925358384.jpeg" alt="Arivu Foundation — Transforming Lives Through Education" fill className="object-contain p-4" priority />
                                                                        </div>
                                                                                        </div>
                                                                                                        <div className="absolute -bottom-6 -left-8 bg-white rounded-2xl shadow-card p-4 min-w-[150px]">
                  <div className="text-2xl font-bold text-saffron">1,200+</div>
                  <div className={`text-xs text-neutral-500 mt-0.5 ${kn ? 'font-kannada' : ''}`}>{kn ? 'ಜೀವನ ಪ್ರಭಾವ' : 'Lives Impacted'}</div>
                </div>
                <div className="absolute -top-6 -right-8 bg-white rounded-2xl shadow-card p-4 min-w-[140px]">
                  <div className="text-2xl font-bold text-indigo">60+</div>
                  <div className={`text-xs text-neutral-500 mt-0.5 ${kn ? 'font-kannada' : ''}`}>{kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನಗಳು' : 'Scholarships Given'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 text-xs">
            <span>{kn ? 'ಅನ್ವೇಷಿಸಲು ಸ್ಕ್ರಾಲ್ ಮಾಡಿ' : 'Scroll to explore'}</span>
            <div className="w-5 h-8 rounded-full border border-white/30 flex items-start justify-center pt-1.5">
              <div className="w-1 h-2 rounded-full bg-white/50 animate-bounce"></div>
            </div>
          </div>
        </section>

        {/* ── Impact Stats ── */}
        <section className="py-16 bg-white border-b border-neutral-100">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {impactStats.map((stat) =>
              <div key={stat.label} className="text-center group">
                  <div className="text-4xl mb-3">{stat.icon}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-indigo mb-1">
                    <CounterNumber target={stat.number} />
                  </div>
                  <div className={`text-neutral-600 text-sm font-medium ${kn ? 'font-kannada' : ''}`}>{stat.label}</div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Mission Strip ── */}
        <section className="py-14 bg-saffron-pale">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="section-divider w-16 mx-auto mb-6"></div>
            <h2 className={`text-3xl sm:text-4xl font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>
              {t.about.missionTitle}
            </h2>
            <p className={`text-neutral-700 text-lg leading-relaxed ${kn ? 'font-kannada' : ''}`}>
              {t.about.missionText}
            </p>
          </div>
        </section>

        {/* ── Programs ── */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="section-divider w-16 mx-auto mb-6"></div>
              <h2 className={`text-3xl sm:text-4xl font-bold text-indigo mb-3 ${kn ? 'font-kannada' : ''}`}>{t.home.programsTitle}</h2>
              <p className={`text-neutral-600 max-w-2xl mx-auto ${kn ? 'font-kannada' : ''}`}>{t.home.programsSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {programs.map((prog, i) =>
              <Link key={prog.title} href={prog.href} className={`group relative rounded-2xl p-7 border card-hover hover-lift ${prog.bg} ${prog.border} ${i === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
                  <div className="text-4xl mb-4">{prog.icon}</div>
                  <h3 className={`text-xl font-bold text-neutral-900 mb-3 ${kn ? 'font-kannada' : ''}`}>{prog.title}</h3>
                  <p className={`text-neutral-600 text-sm leading-relaxed mb-5 ${kn ? 'font-kannada' : ''}`}>{prog.desc}</p>
                  <div className={`flex items-center gap-2 text-sm font-semibold text-saffron group-hover:gap-3 transition-all ${kn ? 'font-kannada' : ''}`}>
                    {t.home.learnMore}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </div>
                  <div className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl bg-gradient-to-r ${prog.color} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                </Link>
              )}
            </div>
            <div className="text-center mt-10">
              <Link href="/programs" className={`inline-flex items-center gap-2 px-8 py-3.5 btn-indigo rounded-full font-semibold text-sm ${kn ? 'font-kannada' : ''}`}>
                {kn ? 'ಎಲ್ಲಾ ಕಾರ್ಯಕ್ರಮಗಳನ್ನು ನೋಡಿ' : 'View All Programs'}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ── How Funds Are Used ── */}
        <section className="py-20 bg-indigo text-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="section-divider w-16 mb-6"></div>
                <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${kn ? 'font-kannada' : ''}`}>{t.home.fundTitle}</h2>
                <p className={`text-white/70 leading-relaxed mb-8 ${kn ? 'font-kannada' : ''}`}>{t.home.fundSubtitle}</p>
                <Link href="/resources" className={`inline-flex items-center gap-2 px-6 py-3 bg-saffron rounded-full font-semibold text-sm hover:bg-saffron-light transition-colors ${kn ? 'font-kannada' : ''}`}>
                  {kn ? 'ವಾರ್ಷಿಕ ವರದಿಗಳನ್ನು ನೋಡಿ' : 'View Annual Reports'}
                </Link>
              </div>
              <div className="space-y-5">
                {fundItems.map((item) =>
                <div key={item.label}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className={`text-white/80 ${kn ? 'font-kannada' : ''}`}>{item.label}</span>
                      <span className="font-bold text-white">{item.pct}%</span>
                    </div>
                    <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.pct}%` }}></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Testimonials ── */}
        <section className="py-20 bg-surface-warm">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="section-divider w-16 mx-auto mb-6"></div>
              <h2 className={`text-3xl font-bold text-indigo mb-2 ${kn ? 'font-kannada' : ''}`}>{t.home.testimonialTitle}</h2>
              <p className={`text-neutral-500 text-sm ${kn ? 'font-kannada' : ''}`}>{t.home.testimonialSubtitle}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((item) =>
              <div key={item.name} className="bg-white rounded-2xl p-7 shadow-card hover-lift">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) =>
                  <svg key={i} className="w-4 h-4 text-saffron" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                  )}
                  </div>
                  <p className={`text-neutral-700 text-sm leading-relaxed mb-5 italic ${kn ? 'font-kannada' : ''}`}>"{item.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-saffron to-crimson flex items-center justify-center text-white font-bold text-sm">{item.initial}</div>
                    <div>
                      <div className="font-semibold text-neutral-900 text-sm">{item.name}</div>
                      <div className={`text-neutral-500 text-xs ${kn ? 'font-kannada' : ''}`}>{item.role}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Donate CTA ── */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="bg-gradient-to-br from-saffron-pale via-white to-indigo-pale rounded-3xl p-12 border border-saffron/10">
              <div className="text-5xl mb-5">🙏</div>
              <h2 className={`text-3xl sm:text-4xl font-bold text-indigo mb-3 ${kn ? 'font-kannada' : ''}`}>{t.home.ctaTitle}</h2>
              <p className={`text-neutral-600 mb-8 max-w-xl mx-auto ${kn ? 'font-kannada' : ''}`}>{t.home.ctaSubtitle}</p>
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {['₹500', '₹1,000', '₹2,500', '₹5,000', kn ? 'ಕಸ್ಟಮ್' : 'Custom'].map((amt) =>
                <Link key={amt} href="/donate" className={`px-5 py-2.5 rounded-full border-2 border-saffron text-saffron font-semibold text-sm hover:bg-saffron hover:text-white transition-all ${kn ? 'font-kannada' : ''}`}>{amt}</Link>
                )}
              </div>
              <Link href="/donate" className={`inline-flex items-center gap-2 px-10 py-4 btn-saffron rounded-full font-bold text-base ${kn ? 'font-kannada' : ''}`}>
                {kn ? 'ದೇಣಿಗೆ ನೀಡಿ — 80G ತೆರಿಗೆ ಪ್ರಯೋಜನ' : 'Donate Now — 80G Tax Benefit'}
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
              </Link>
              <p className={`text-neutral-400 text-xs mt-4 ${kn ? 'font-kannada' : ''}`}>{kn ? 'ಆದಾಯ ತೆರಿಗೆ ಕಾಯ್ದೆ ಅಡಿ 80G ತೆರಿಗೆ ಕಡಿತಕ್ಕೆ ದೇಣಿಗೆಗಳು ಅರ್ಹ' : 'Donations are eligible for 80G tax deduction under the Income Tax Act'}</p>
            </div>
          </div>
        </section>

        {/* ── Get Involved ── */}
        <section className="py-16 bg-surface-muted">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className={`text-3xl font-bold text-indigo mb-2 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.badge}</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
              { title: kn ? 'ಸ್ವಯಂಸೇವಕ' : 'Volunteer', desc: kn ? 'ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳಿಗೆ ನಿಮ್ಮ ಸಮಯ ಮತ್ತು ಕೌಶಲ್ಯ ಮೀಸಲಿಡಿ.' : 'Dedicate your time and skills to our programs.', href: '/get-involved', icon: '🤝', color: 'text-saffron' },
              { title: kn ? 'ಪಾಲುದಾರರಾಗಿ' : 'Partner With Us', desc: kn ? 'CSR ಪಾಲುದಾರಿಕೆ ಮತ್ತು ಸಾಂಸ್ಥಿಕ ಸಹಯೋಗ.' : 'CSR partnerships and institutional collaborations.', href: '/get-involved', icon: '🏢', color: 'text-indigo' },
              { title: kn ? 'ಹರಡಿ' : 'Spread the Word', desc: kn ? 'ನಿಮ್ಮ ನೆಟ್‌ವರ್ಕ್‌ನೊಂದಿಗೆ ನಮ್ಮ ಮಿಷನ್ ಹಂಚಿಕೊಳ್ಳಿ.' : 'Share our mission with your network.', href: '/news', icon: '📢', color: 'text-crimson' }].
              map((item) =>
              <Link key={item.title} href={item.href} className="bg-white rounded-2xl p-7 text-center hover-lift card-hover group">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className={`text-lg font-bold mb-3 ${item.color} ${kn ? 'font-kannada' : ''}`}>{item.title}</h3>
                  <p className={`text-neutral-600 text-sm ${kn ? 'font-kannada' : ''}`}>{item.desc}</p>
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>);

}